import {
  IoArrowBack,
  IoReload,
  IoCheckmark,
  IoCloudDownloadOutline,
  IoTrash,
  IoCubeOutline,
  IoCodeSlashOutline,
} from "react-icons/io5";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { BsCpu } from "react-icons/bs";
import { SiMicrosoftexcel } from "react-icons/si";
import { TbCloudDownload } from "react-icons/tb";
import { PiFilesDuotone } from "react-icons/pi";
import { LuDatabase } from "react-icons/lu";
import { Card } from "../../../styles";
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "@hooks/auth";
import RevisorDownloads from "./download";
import { IoAlert } from "react-icons/io5";
import ProcessingStateDisplay from "./processing";

export default function Revisor({ handleClose }) {
  const { projeto, token } = useAuth();
  const [files, setFiles] = useState([]);
  const [isProcessingTasks, setIsProcessingTasks] = useState(false);
  const [processingCompleted, setProcessingCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null); // Novo estado para mensagem de erro
  const [tasks, setTasks] = useState([
    {
      id: 1,
      name: "Enviando arquivos",
      progress: 0,
      status: "pending",
      icon: <AiOutlineCloudUpload />,
    },
    {
      id: 2,
      name: "Processando arquivos",
      progress: 0,
      status: "pending",
      icon: <BsCpu />,
    },
    {
      id: 3,
      name: "Gerando relatórios",
      progress: 0,
      status: "pending",
      icon: <SiMicrosoftexcel />,
    },
  ]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files)
      .filter((file) => file.type === "text/plain")
      .map((file) => ({ file, status: "pending", progress: 0 }));
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const newFiles = Array.from(event.dataTransfer.files)
      .filter((file) => file.type === "text/plain")
      .map((file) => ({ file, status: "pending", progress: 0 }));
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!token) {
      toast.error("Usuário não autenticado. Por favor, faça login novamente.");
      return;
    }

    if (files.length === 0) {
      toast.error("Nenhum arquivo selecionado para enviar.");
      return;
    }

    // Limpa a mensagem de erro antes de iniciar o processamento
    setErrorMessage(null);

    setIsProcessingTasks(true);
    const formData = new FormData();
    files.forEach((fileData) => formData.append("files", fileData.file));

    try {
      await axios.post(`${process.env.REACT_APP_API}/tools/revisor`, formData, {
        headers: { Authorization: `Bearer ${token}`, projeto: projeto._id },
      });

      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === 1 ? { ...task, progress: 100, status: "completed" } : task
        )
      );

      const ws = new WebSocket("wss://log.vidal-app.com/ws");
      ws.onopen = () =>
        ws.send(
          JSON.stringify({
            action: "subscribe",
            projectId: projeto._id,
            type: "revisor",
          })
        );

      ws.onmessage = (event) => {
        const messageData = JSON.parse(event.data);
        const { type, projectId, data } = messageData.message || {};

        if (projectId === projeto._id && type === "revisor") {
          if (data.error) {
            setErrorMessage(data.error); // Captura a mensagem de erro
            setIsProcessingTasks(false);
            return;
          }

          updateTaskProgress(data);

          // Verifica se todas as tarefas foram concluídas
          if (
            data.tasks &&
            data.tasks.every((task) => task.status === "completed")
          ) {
            setProcessingCompleted(true);
            ws.close();
          }
        }
      };

      ws.onerror = () => {
        toast.error("Erro no processamento dos arquivos.");
        setIsProcessingTasks(false);
      };
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro no envio dos arquivos"
      );
      setIsProcessingTasks(false);
    }
  };

  const updateTaskProgress = (data) => {
    if (data.tasks) {
      // Primeiro, obtenha o estado atual das tarefas
      const currentTasks = [...tasks];

      // Encontre a tarefa ativa atual (primeira tarefa não concluída)
      const activeTaskIndex = currentTasks.findIndex(
        (task) => task.status !== "completed"
      );

      // Se todas as tarefas estiverem concluídas, apenas atualize normalmente
      if (activeTaskIndex === -1) {
        setTasks((prevTasks) =>
          prevTasks.map((task) => {
            const updatedTask = data.tasks.find((t) => t.id === task.id);
            return updatedTask
              ? {
                  ...task,
                  progress: updatedTask.progress,
                  status: updatedTask.status,
                }
              : task;
          })
        );
        return;
      }

      // Para cada tarefa recebida do servidor
      setTasks((prevTasks) => {
        // Clone para não modificar o estado diretamente
        const newTasks = [...prevTasks];

        // Primeiro, atualize apenas as tarefas já concluídas
        data.tasks.forEach((serverTask) => {
          const taskIndex = newTasks.findIndex((t) => t.id === serverTask.id);
          if (taskIndex !== -1 && newTasks[taskIndex].status === "completed") {
            newTasks[taskIndex] = {
              ...newTasks[taskIndex],
              progress: 100,
              status: "completed",
            };
          }
        });

        // Agora atualize a tarefa ativa atual
        const serverActiveTask = data.tasks.find(
          (t) => t.id === newTasks[activeTaskIndex].id
        );
        if (serverActiveTask) {
          newTasks[activeTaskIndex] = {
            ...newTasks[activeTaskIndex],
            progress: serverActiveTask.progress,
            status: serverActiveTask.status,
          };
        }

        // Se a tarefa ativa foi concluída, ative a próxima tarefa
        if (
          newTasks[activeTaskIndex].status === "completed" &&
          activeTaskIndex < newTasks.length - 1
        ) {
          // Verifique se o servidor já está marcando a próxima tarefa como em andamento
          const nextServerTask = data.tasks.find(
            (t) => t.id === newTasks[activeTaskIndex + 1].id
          );
          if (nextServerTask && nextServerTask.status === "in-progress") {
            newTasks[activeTaskIndex + 1] = {
              ...newTasks[activeTaskIndex + 1],
              progress: nextServerTask.progress,
              status: nextServerTask.status,
            };
          }
        }

        return newTasks;
      });
    }
  };

  const resetComponent = () => {
    setFiles([]);
    setTasks([
      {
        id: 1,
        name: "Enviando arquivos",
        progress: 0,
        status: "pending",
        icon: <AiOutlineCloudUpload />,
      },
      {
        id: 2,
        name: "Processando arquivos",
        progress: 0,
        status: "pending",
        icon: <BsCpu />,
      },
      {
        id: 3,
        name: "Gerando relatórios",
        progress: 0,
        status: "pending",
        icon: <SiMicrosoftexcel />,
      },
      {
        id: 4,
        name: "Baixando arquivos",
        progress: 0,
        status: "pending",
        icon: <IoCloudDownloadOutline />,
      },
    ]);
    setIsProcessingTasks(false);
    setProcessingCompleted(false);
    setErrorMessage(null); // Limpa a mensagem de erro ao resetar
  };

  return (
    <Card>
      <div className="header">
        <div className="btn" onClick={handleClose}>
          <IoArrowBack />
        </div>
        <IoCodeSlashOutline />
        <p>Executar script</p>
      </div>

      <div className="display">
        <div className="item ativ">
          <div className="thumb">
            <TbCloudDownload />
          </div>
          <div className="content">
            <p>Relatório de Escrituração</p>
            <p>Processa arquivos SPEDs e gera relatórios estruturados.</p>
          </div>
        </div>

        {!isProcessingTasks ? (
          <>
            {files.length > 0 && (
              <div className="tag">
                <div className="info">
                  <PiFilesDuotone />
                  <p>{files.length}</p>
                </div>
                <div className="info">
                  <LuDatabase />
                  <p>
                    {(
                      files.reduce((acc, file) => acc + file.file.size, 0) /
                      (1024 * 1024)
                    ).toFixed(2)}{" "}
                    MB
                  </p>
                </div>
                <div className="btn" onClick={() => setFiles([])}>
                  <IoReload />
                </div>
              </div>
            )}
            <div className="drop">
              <div
                className="drop-area"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current.click()}
              >
                <div className="content">
                  <div className="thumb">
                    <p>+</p>
                  </div>
                  <div className="text">
                    <p>Carregar arquivos</p>
                    <p>Arraste para cá o SPED que deseja processar</p>
                  </div>
                </div>
              </div>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </div>
            {files.length > 0 && (
              <div className="files">
                {files.map((fileData, index) => (
                  <div className="file" key={index}>
                    <div className="thumb">
                      {fileData.status === "uploading" && (
                        <div className="spinner" />
                      )}
                      {fileData.status === "completed" && <IoCheckmark />}
                      {fileData.status === "pending" && <IoCubeOutline />}
                    </div>
                    <div className="content">
                      <p>{fileData.file.name}</p>
                      <p className="size">
                        {(fileData.file.size / 1024).toFixed(2)} Kb
                      </p>
                      <IoTrash onClick={() => handleRemoveFile(index)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : processingCompleted ? (
          <div className="completed">
            <div className="header">
              <IoCheckmark color="#4eb959" />
              <span>Concluído</span>
            </div>

            <div className="info">
              <p>Arquivos processados:</p>
              {files.map((fileData, index) => (
                <div key={index}>
                  <span>{fileData.file.name}</span> -{" "}
                  <span>{(fileData.file.size / 1024).toFixed(2)} KB</span>
                </div>
              ))}
            </div>

            <RevisorDownloads
              token={token}
              projectId={projeto._id}
              isProcessingComplete={processingCompleted}
            />
          </div>
        ) : (
          <ProcessingStateDisplay tasks={tasks} />
        )}
      </div>
      {errorMessage && ( // Exibe a mensagem de erro se existir
        <div className="error-message">
          <IoAlert color="#ff4444" />
          <p>{errorMessage}</p>
        </div>
      )}
      <div className="bottom">
        {!isProcessingTasks && (
          <div className="btn" onClick={handleSend}>
            <p>EXECUTAR</p>
          </div>
        )}
      </div>
    </Card>
  );
}
